// ============================================================================
// LE BLOC DE SCAN — le même partout où l'on bipe, sans exception.
//
// Le cadre vert, l'icône de VISÉE (`barcode-frame`), le champ où l'on bipe, l'indice à droite, et la
// ligne de RETOUR du dernier bip. Relevé de la maquette back-office, où il apparaît à l'identique sur
// les réceptions et sur l'inventaire — mêmes dimensions, mêmes tons, même DOM.
//
// ⚠️ POURQUOI IL VIT DANS LA CHARTE ET NON DANS UNE APP (demande utilisateur 2026-09-03). Il existait
// en double : transposé à la main sur les réceptions, puis recopié pour l'inventaire. Deux copies,
// c'est la dérive de demain — une hauteur de champ qui change ici et pas là, un ton d'alerte qui
// diverge, et surtout un GESTE qui ne se comporte plus pareil selon l'écran. Or celui-là s'exécute la
// douchette en main, SANS REGARDER L'ÉCRAN : c'est précisément le geste qui ne supporte pas la
// surprise. Ici, il est disponible à toute surface web — et une seule décision le gouverne.
//
// ⚠️ IL NE SAIT RIEN DU MÉTIER, et c'est ce qui le rend partageable : ni article, ni zone, ni bon de
// livraison. Il rend un champ, prévient quand on valide, et affiche ce que l'appelant lui dit
// d'afficher. Toute la décision — que faire du code bipé — reste chez l'appelant.
//
// ⚠️ « CHAQUE BIP = +1 » EST UNE RÈGLE D'APPELANT, PAS D'ICI. Les réceptions incrémentent le contrôlé,
// l'inventaire incrémente le comptage d'une zone : même geste, deux écritures. Les confondre ici y
// ferait entrer du métier, et la troisième surface ne pourrait plus l'employer.
// ============================================================================
import { useEffect, useRef, type ReactNode } from 'react';
import { Icon } from './icons';

const MONO = 'var(--font-mono)';

/** Le ton du retour de bip — un RÔLE, jamais une couleur choisie par l'appelant. */
export type ScanTone = 'ok' | 'warn' | 'err';

/** Ce que le dernier bip a produit, tel qu'on le rend à l'opérateur. */
export interface ScanFeedback {
  tone: ScanTone;
  text: string;
  /** Bouton d'issue quand le bip pose une question (« ce code n'est pas au bon — l'ajouter ? »). */
  action?: { label: string; onClick: () => void };
}

const TONES: Record<ScanTone, { bg: string; fg: string; icon: 'check' | 'alert' }> = {
  ok: { bg: 'var(--teal-100)', fg: 'var(--teal-800)', icon: 'check' },
  warn: { bg: 'var(--amber-bg)', fg: 'var(--amber-fg)', icon: 'alert' },
  err: { bg: 'var(--rose-bg)', fg: 'var(--rose-fg)', icon: 'alert' },
};

export interface ScanBlockProps {
  value: string;
  onChange: (v: string) => void;
  /** Appelé sur Entrée — c'est le retour chariot que toute douchette envoie après le code. */
  onSubmit: () => void;
  placeholder?: string;
  /** L'indice à droite du champ : ce qu'on attend, ou ce qui vient de se passer. */
  hint?: ReactNode;
  /**
   * Ce qui s'insère ENTRE l'icône et le champ — le sélecteur de zone de l'inventaire, par exemple.
   *
   * ⚠️ UN EMPLACEMENT, PAS UNE OPTION MÉTIER : ce fichier ne doit pas apprendre ce qu'est une zone,
   * sinon la surface suivante devra y ajouter la sienne, et le bloc cessera d'être partageable.
   */
  before?: ReactNode;
  /**
   * Ce qui se place APRÈS l’indice, au bout de la rangée — un geste voisin du scan.
   *
   * ⚠️ MÊME RAISON QUE « before » : un EMPLACEMENT, jamais une option métier. Le premier emploi est le
   * bouton « Retrouver un article » de l’inventaire, qui répond à la question qu’on se pose la
   * douchette en main. Le loger ailleurs ferait quitter l’écran de comptage pour y revenir.
   * ⚠️ IL NE SE TERNIT PAS AVEC LE CHAMP : chercher reste possible quand biper ne l’est pas encore.
   */
  after?: ReactNode;
  /** Le retour du dernier bip, sous le cadre. */
  last?: ScanFeedback | null;
  /**
   * Reprend le focus à chaque changement de cette valeur.
   *
   * ⚠️ INDISPENSABLE À LA DOUCHETTE : elle écrit là où est le curseur. Un clic ailleurs, une modale
   * qui se ferme, et les bips suivants partent dans le vide — l'opérateur scanne une rangée entière
   * sans que rien ne soit compté, et ne s'en aperçoit qu'au total.
   */
  focusOn?: unknown;
  disabled?: boolean;
}

export function ScanBlock({
  value, onChange, onSubmit, placeholder, hint, before, after, last, focusOn, disabled,
}: ScanBlockProps) {
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => { if (!disabled) field.current?.focus(); }, [focusOn, disabled]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 11, padding: '10px 13px', borderRadius: 'var(--r-sm)',
        background: 'var(--teal-100)', border: '1.5px solid var(--teal-400)', flexWrap: 'wrap',
      }}>
        {/* ⚠️ L'ICÔNE DE VISÉE, pas celle du code-barres : ici on ne DÉSIGNE pas un gencode, on annonce
            le geste de le viser. Les deux dessins existent pour cette distinction exacte.
            ⚠️ 26 px, ET LE TRAIT ÉPAISSI AVEC (demande utilisateur 2026-09-03, choisi au rendu). La
            taille n'a pas de maximum — le dessin est vectoriel — mais le TRAIT est calibré à 1,75 pour
            une grille de 24 : agrandir sans l'épaissir donne une icône grêle. Les deux vont ensemble.
            ⚠️ ON RESTE SOUS LA HAUTEUR DU CHAMP (38 px) : au-delà, l'icône fait grandir la rangée et
            cesse d'être un repère pour devenir le sujet. */}
        <span style={{ display: 'flex', color: 'var(--teal-800)', flexShrink: 0, opacity: disabled ? 0.5 : 1 }}>
          <Icon name="barcode-frame" size={26} stroke={1.9} />
        </span>
        {/* ⚠️ CE QUI PRÉCÈDE LE CHAMP NE SE TERNIT JAMAIS (corrigé 2026-09-04, vu à l'écran) : c'est
            souvent LA SORTIE de l'état inerte — le sélecteur de zone de l'inventaire, sans lequel le
            champ ne s'active pas. L'effacer avec le reste éteindrait la seule chose à faire. */}
        {before}
        <input
          ref={field} value={value} disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSubmit(); } }}
          placeholder={placeholder ?? 'Scannez ici — douchette ou saisie du code-barres, Entrée pour biper'}
          style={{
            flex: 1, minWidth: 230, height: 38, padding: '0 12px',
            border: '1px solid var(--teal-400)', borderRadius: 'var(--r-ctrl)',
            background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13px', fontFamily: MONO, outline: 'none',
            opacity: disabled ? 0.55 : 1, cursor: disabled ? 'not-allowed' : undefined,
          }}
        />
        {hint ? (
          <span style={{ fontSize: '11.5px', color: 'var(--teal-800)', fontWeight: 600, whiteSpace: 'nowrap', opacity: disabled ? 0.7 : 1 }}>
            {hint}
          </span>
        ) : null}
        {after}
      </div>
      {last ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderRadius: 'var(--r-ctrl)',
          fontSize: '12.5px', fontWeight: 600,
          background: TONES[last.tone].bg, color: TONES[last.tone].fg,
        }}>
          <Icon name={TONES[last.tone].icon} size={14} />
          <span style={{ flex: 1, minWidth: 0 }}>{last.text}</span>
          {last.action ? (
            <button type="button" onClick={last.action.onClick}
              style={{ height: 28, padding: '0 12px', border: 'none', borderRadius: 'var(--r-xs)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0, cursor: 'pointer' }}>
              {last.action.label}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
